import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

/**
 * SkeletonLoader - Reusable skeleton loading component
 * 
 * @param {Object} props
 * @param {string} props.variant - 'table' | 'card' | 'list' | 'text' | 'circular'
 * @param {number} props.count - Number of skeleton items to show
 * @param {number} props.height - Height of skeleton items
 * @param {Object} props.sx - Additional styling
 */
const SkeletonLoader = ({ 
  variant = 'card', 
  count = 3, 
  height,
  sx = {}
}) => {
  const getSkeletonProps = () => {
    const baseProps = {
      animation: 'wave',
      sx: {
        bgcolor: 'rgba(0, 0, 0, 0.06)',
        ...sx,
      },
    };

    switch (variant) {
      case 'table':
        return {
          ...baseProps,
          variant: 'rectangular',
          height: height || 56,
          width: '100%',
        };
      
      case 'card':
        return {
          ...baseProps,
          variant: 'rectangular',
          height: height || 200,
          width: '100%',
          sx: {
            ...baseProps.sx,
            borderRadius: 2,
          },
        };
      
      case 'list':
        return {
          ...baseProps,
          variant: 'rectangular',
          height: height || 60,
          width: '100%',
          sx: {
            ...baseProps.sx,
            borderRadius: 1,
          },
        };
      
      case 'text':
        return {
          ...baseProps,
          variant: 'text',
          width: '100%',
          height: height || 24,
        };
      
      case 'circular':
        return {
          ...baseProps,
          variant: 'circular',
          width: height || 40,
          height: height || 40,
        };
      
      default:
        return baseProps;
    }
  };

  const skeletonProps = getSkeletonProps();

  if (variant === 'table') {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Stack spacing={1}>
          {/* Header row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" width="25%" height={40} />
            ))}
          </Box>
          {/* Data rows */}
          {Array.from({ length: count }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" width="25%" height={56} />
              ))}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (variant === 'card') {
    return (
      <Box sx={{ width: '100%' }}>
        <Stack spacing={2}>
          {Array.from({ length: count }).map((_, index) => (
            <Box key={index} sx={{ p: 2 }}>
              <Skeleton {...skeletonProps} />
              <Box sx={{ mt: 2 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (variant === 'list') {
    return (
      <Box sx={{ width: '100%' }}>
        <Stack spacing={1.5}>
          {Array.from({ length: count }).map((_, index) => (
            <Skeleton key={index} {...skeletonProps} />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
      <Skeleton {...skeletonProps} />
    </Box>
  );
};

export default SkeletonLoader;

